<script>
import { computed, ref, watch } from 'vue'
import _clone from 'lodash/clone'
import {
  formatPrice,
  vatPrice,
  roundPrice,
} from '../../../../../../Greativ/helper/price'

import { currency } from '@/stores/basket'
import { updateProduct, removeProduct } from '@/stores/product'

import LazyPictureTag from '@/components/LazyPictureTag'

export default {
  components: {
    LazyPictureTag,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const amount = ref(0)
    watch(
      () => props.product,
      (product, oldProduct) => {
        if (product.amount !== oldProduct?.amount) {
          amount.value = product.amount
        }
      },
      {
        immediate: true,
      }
    )

    const changeAmount = (value) => {
      let newValue = _clone(amount.value)
      if (isNaN(newValue)) {
        newValue = 1
      }
      const step = props.product.amountStep || 1

      if (value === 'inc') {
        newValue += step
      } else if (value === 'dec') {
        newValue -= step
      } else if (step && newValue % step !== 0) {
        newValue += step - (newValue % step)
      }

      let min = props.product.minAmount || 1
      if (step && min % step !== 0) {
        min += step - (min % step)
      }

      let max = props.product.maxAmount || props.product.inventory
      if (step && max % step !== 0) {
        max -= max % step
      }

      if (newValue > max) {
        newValue = max
      }

      if (newValue < min) {
        newValue = min
      }

      amount.value = newValue
      updateProduct(props.product.uID, newValue)
    }

    const singlePrice = computed(() => {
      if (props.product.extraDiscount !== null) {
        return vatPrice(props.product.extraDiscount, props.product.vat)
      }

      if (props.product.discountPrice) {
        return vatPrice(props.product.discountPrice, props.product.vat)
      }

      return vatPrice(props.product.price, props.product.vat)
    })

    const totalPrice = computed(() => {
      if (props.product.extraDiscount !== null) {
        return roundPrice(
          vatPrice(props.product.extraDiscount, props.product.vat) *
            props.product.amount
        )
      }

      if (props.product.discountPrice) {
        return roundPrice(
          vatPrice(props.product.discountPrice, props.product.vat) *
            props.product.amount
        )
      }

      return (
        roundPrice(vatPrice(props.product.price, props.product.vat)) *
        props.product.amount
      )
    })

    const realTotalPrice = computed(() => {
      if (props.product.extraDiscount === null) {
        return null
      }

      return (
        roundPrice(vatPrice(props.product.price, props.product.vat)) *
        props.product.amount
      )
    })

    const maxAmount = computed(() => {
      return props.product.maxAmount || props.product.inventory
    })

    return {
      formatPrice,
      vatPrice,
      currency,
      amount,
      maxAmount,
      changeAmount,
      removeProduct,
      singlePrice,
      totalPrice,
      realTotalPrice,
    }
  },
}
</script>

<template>
  <div
    class="flex items-center border-0 border-b-2px border-solid border-gray-700"
  >
    <div class="mr-10 w-56 flex-shrink-0 py-3">
      <div class="image relative pb-100%">
        <LazyPictureTag
          fallbackImage="ui/template/rallyestonia/images/no-image-placeholder-with-space.svg"
          :fallbackImageWidth="140"
          :fallbackImageHeight="140"
          :fallbackImageAlt="product.productName"
          loadingImage="ui/template/rallyestonia/images/no-image-placeholder-with-space.svg"
          class="absolute inset-0 m-auto h-full !max-h-100% w-full !max-w-100% object-contain"
          :sizes="
            product.image
              ? [
                  {
                    mediaRule: 300,
                    url: product.image,
                    config: {
                      w: 160,
                      h: 160,
                      fit: 'max',
                    },
                  },
                ]
              : []
          "
        />
      </div>
    </div>

    <div
      class="flex-grow-1 lg:flex-center flex flex-col items-start lg:flex-row lg:items-center"
    >
      <div class="flex-1 px-10 py-3">
        <div class="text-7 font-black">
          {{ product.productName }}
          <div v-if="product.parameters.length">
            <div
              class="font-normal"
              v-for="(parameter, parameterIndex) in product.parameters"
              :key="`pp${parameterIndex}`"
            >
              {{ parameter.name }}: {{ parameter.value }}
            </div>
          </div>
        </div>

        <div class="hidden text-7 font-black text-red lg:block">
          {{ formatPrice(singlePrice) }}
          {{ currency }}
        </div>

        <div
          class="text-red"
          v-if="
            product.inventory &&
            product.inventory < product.amount &&
            product.barCode !== 'RALLY_PASS' &&
            product.barCode !== 'RALLY_PASS_PARTNER'
          "
        >
          {{ __('available_quantity', { count: product.inventory }) }}
        </div>
      </div>

      <div class="flex-shrink-0 px-10 py-3 lg:w-80">
        <div
          class="relative mx-auto w-40"
          v-if="
            product.barCode !== 'RALLY_PASS' &&
            product.barCode !== 'RALLY_PASS_PARTNER' &&
            maxAmount !== 1
          "
        >
          <input
            type="text"
            class="focus:outline-none h-18 w-full appearance-none border border-solid bg-gray pr-8 text-center text-7 font-black"
            :class="
              product.inventory < product.amount
                ? 'border-red text-red'
                : 'border-gray text-black'
            "
            :disabled="locked"
            v-model.number="amount"
            @change="changeAmount($event.target.value)"
          />
          <button
            type="button"
            class="btn-change-amount"
            :disabled="locked"
            @click="changeAmount('inc')"
            v-if="maxAmount > amount"
          ></button>
          <button
            type="button"
            class="btn-change-amount dec"
            :disabled="locked"
            @click="changeAmount('dec')"
            v-if="amount > 1"
          ></button>
        </div>
        <div class="text-center text-7 font-black" v-else>
          {{ product.amount }}
        </div>
      </div>
      <div class="px-10 py-3 text-7 font-black text-red lg:w-72 lg:text-center">
        {{ formatPrice(totalPrice) }}
        {{ currency }}

        <div
          class="text-80% text-black line-through"
          v-if="realTotalPrice && realTotalPrice > totalPrice"
        >
          {{ formatPrice(realTotalPrice) }} {{ currency }}
        </div>
      </div>
    </div>

    <div class="ml-auto py-3 pl-16">
      <button
        type="button"
        class="remove-product"
        :title="__('btn_remove')"
        :disabled="locked"
        @click.prevent="removeProduct(product.uID)"
      ></button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.remove-product {
  position: relative;
  width: 4rem;
  height: 13rem;
  background-color: #eeeeee;
  margin-left: auto;
  border: none;
  transition: background-color 300ms ease-in-out;
  padding: 0;

  &:hover {
    background-color: #dfdfdf;
  }

  &:focus {
    outline: none;
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    width: 0.4rem;
    height: 3rem;
    margin: auto;
    background-color: #181818;
  }

  &:before {
    transform: rotate(45deg);
  }

  &:after {
    transform: rotate(-45deg);
  }
}

.btn-change-amount {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 0;
  height: 0;
  padding: 0;
  appearance: none;
  border-style: solid !important;
  border-width: 0 0.5rem 0.5rem 0.5rem !important;
  border-color: transparent transparent #181818 transparent !important;

  &:focus {
    outline: none;
  }

  &.dec {
    top: auto;
    bottom: 1rem;
    border-width: 0.5rem 0.5rem 0 0.5rem !important;
    border-color: #181818 transparent transparent transparent !important;
  }
}
</style>
